import React from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar,
  Eye,
  ListChecks,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminSecurity } from '../../hooks/useAdminSecurity';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminSecurity = () => {
  const {
    securityLogs,
    isLoading,
    error,
    filters,
    activityTypeOptions,
    severityOptions,
    handleFilterChange,
    handleSearch,
    getSeverityColor,
    statistics: stats
  } = useAdminSecurity();

  const columns = [
    {
      key: 'id',
      label: 'Log ID',
      sortable: true,
      width: 'w-32',
      render: (log) => (
        <span className="font-mono text-sm font-medium text-primary-600 dark:text-primary-400">
          #{log.id.substring(0, 8)}
        </span>
      )
    },
    {
      key: 'user',
      label: 'User',
      sortable: true,
      width: 'w-48',
      render: (log) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
            {log.user?.name || 'System'}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {log.user?.email || ''}
          </p>
        </div>
      )
    },
    {
      key: 'activity_type',
      label: 'Event Type',
      sortable: true,
      width: 'w-40',
      render: (log) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-50 capitalize">
          {log.activity_type?.replace('_', ' ') || 'N/A'}
        </span>
      )
    },
    {
      key: 'activity_description',
      label: 'Description',
      sortable: false,
      width: 'w-64',
      render: (log) => (
        <p className="text-sm text-gray-900 dark:text-gray-50 truncate">
          {log.activity_description || 'N/A'}
        </p>
      )
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      sortable: true,
      width: 'w-32',
      render: (log) => (
        <span className="font-mono text-sm text-gray-900 dark:text-gray-50">
          {log.ip_address || 'N/A'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-32',
      render: (log) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {formatDate(log.created_at, 'medium')}
            </div>
            <div className="text-xs text-gray-500">
              {formatTime(log.created_at)}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'severity',
      label: 'Severity',
      sortable: true,
      width: 'w-32',
      render: (log) => {
        const color = getSeverityColor(log.activity_type);
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}>
            {log.activity_type === 'login_failed' || log.activity_type === 'account_locked' || log.activity_type === 'suspicious_activity' || log.activity_type === 'security_alert'
              ? 'High'
              : log.activity_type === 'password_change' || log.activity_type === 'password_reset' || log.activity_type === 'two_factor_enabled' || log.activity_type === 'two_factor_disabled'
              ? 'Medium'
              : 'Low'}
          </span>
        );
      }
    }
  ];

  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      variant: 'default',
      onClick: (log) => {
        console.log('View security log:', log);
      }
    }
  ];

  if (error) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Security Logs"
          subtitle="Monitor security events and authentication activities"
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-2">Error Loading Security Logs</h3>
          <p className="text-gray-600 dark:text-gray-400">
            {error?.response?.data?.message || 'Failed to load security logs. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Security Logs"
        subtitle="Monitor security events and authentication activities"
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.security.title}
        steps={ADMIN_PAGE_GUIDES.security.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Events</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{stats.total_security_events || 0}</p>
            </div>
            <Shield className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Failed Logins (24h)</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1 tabular-nums">{stats.failed_logins_24h || 0}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Successful Logins (24h)</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{stats.successful_logins_24h || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Suspicious Activities (7d)</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1 tabular-nums">{stats.suspicious_activities_7d || 0}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <SearchSelect
            label="Event Type"
            value={filters.activity_type}
            onChange={(value) => handleFilterChange('activity_type', value)}
            options={activityTypeOptions}
          />
          <SearchSelect
            label="Severity"
            value={filters.severity}
            onChange={(value) => handleFilterChange('severity', value)}
            options={severityOptions}
          />
          <DatePicker
            label="From Date"
            value={filters.date_from}
            onChange={(value) => handleFilterChange('date_from', value)}
          />
          <DatePicker
            label="To Date"
            value={filters.date_to}
            onChange={(value) => handleFilterChange('date_to', value)}
          />
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
        <DataTable
          data={securityLogs}
          columns={columns}
          actions={actions}
          isLoading={isLoading}
          searchPlaceholder="Search by user, IP address, or description..."
          onSearch={handleSearch}
          emptyMessage="No security logs found"
        />
      </div>
    </div>
  );
};

export default AdminSecurity;
